// Which image model paints: one choice for the whole app, remembered in this browser, shown as a dropdown on every
// screen that paints something. The api sends it with every painting request.
import { useSyncExternalStore } from "react";
import { DEFAULT_IMAGE_MODEL, IDEOGRAM_PRECISIONS, IDEOGRAM_PRECISION_NAMES, IDEOGRAM_TIERS, IDEOGRAM_TIER_NAMES, IMAGE_MODELS, QUALITIES, QUALITY_NAMES, hasQuality, ideogramSetting, isImageModel, parseIdeogram, qualityFor, type ImageModel, type PaintQuality } from "../../model/imageModels";

const KEY = "image-model";
const listeners = new Set<() => void>();

export function getImageModel(): ImageModel {
  try {
    const v = localStorage.getItem(KEY);
    if (isImageModel(v)) return v;
  } catch {}
  return DEFAULT_IMAGE_MODEL;
}

function setImageModel(m: ImageModel) {
  try { localStorage.setItem(KEY, m); } catch {}
  listeners.forEach(l => l());
}

// Quality is remembered per model, for the models that have the setting (see src/model/imageModels.ts).
const qualityKey = (m: ImageModel) => `image-quality-${m}`;
export function getImageQuality(m: ImageModel): PaintQuality {
  try { return qualityFor(m, localStorage.getItem(qualityKey(m))); } catch {}
  return qualityFor(m, null);
}
function setImageQuality(m: ImageModel, q: PaintQuality) {
  try { localStorage.setItem(qualityKey(m), q); } catch {}
  listeners.forEach(l => l());
}
// Quick view's new scene and character have no dropdown: they paint with one fixed choice, set here while they are open (null: the dropdown's choice).
type PaintChoice = { model: ImageModel; quality?: PaintQuality };
let fixed: PaintChoice | null = null;
export const fixPaintChoice = (c: PaintChoice | null) => { fixed = c; };

// What every painting request carries: the model and, if it has the setting, its quality.
export function paintChoice(): PaintChoice {
  if (fixed) return fixed;
  const model = getImageModel();
  return hasQuality(model) ? { model, quality: getImageQuality(model) } : { model };
}

export const useImageModel = () => useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l); }, getImageModel);

export function ModelSelect({ dark, bare }: { dark?: boolean; bare?: boolean }) {
  const model = useImageModel();
  useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l); }, () => getImageQuality(model));
  const quality = getImageQuality(model);
  const hint = IMAGE_MODELS.find(m => m.id === model)!.hint;
  return (
    <label className={`model-select${dark ? " dark" : ""}${bare ? " bare" : ""}`}>
      <span className="model-field">
        <span className="model-label">Draw with</span>
        <select id="image-model" value={model} onChange={e => isImageModel(e.target.value) && setImageModel(e.target.value)}>
          {IMAGE_MODELS.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </span>
      {model === "ideogram" ? (() => {
        const { tier, precision } = parseIdeogram(quality);
        return <>
          <span className="model-field">
            <span className="model-label">Quality</span>
            <select id="image-quality" value={tier} aria-label="Quality" onChange={e => setImageQuality(model, ideogramSetting(e.target.value as typeof tier, precision))}>
              {IDEOGRAM_TIERS.map(t => <option key={t} value={t}>{IDEOGRAM_TIER_NAMES[t]}</option>)}
            </select>
          </span>
          <span className="model-field">
            <span className="model-label">Edit precision</span>
            <select id="image-precision" value={precision} aria-label="Edit precision" onChange={e => setImageQuality(model, ideogramSetting(tier, e.target.value as typeof precision))}>
              {IDEOGRAM_PRECISIONS.map(p => <option key={p} value={p}>{IDEOGRAM_PRECISION_NAMES[p]}</option>)}
            </select>
          </span>
        </>;
      })() : hasQuality(model) && <>
        <span className="model-field">
          <span className="model-label">Quality</span>
          <select id="image-quality" value={quality} aria-label="Quality" onChange={e => setImageQuality(model, qualityFor(model, e.target.value))}>
            {QUALITIES.map(q => <option key={q} value={q}>{QUALITY_NAMES[q]}</option>)}
          </select>
        </span>
      </>}
      {!bare && <span className="model-hint">{hint}</span>}
    </label>
  );
}
